import type { FormField } from '@lucid/lego';
import type {
  Prospect,
  TestDriveSchedule,
} from '../../../../../api/services/lupp/prospect/dto.prospect';
import { getNextEngagementDate, getLatestNoteContent } from '../utils';
import {
  formatDateTime,
  utcToLocalTimeSlot,
  utcToLocalDate,
  escapeHtml,
} from '../../../../../utils/tools';
import {
  carMakerOptions,
  getEmailConsentOptions,
  getFinanceTypeOptions,
  getPhoneConsentOptions,
  getPreferredContactMethodOptions,
  getPurchaseTimingOptions,
  getPurchaseTypeOptions,
  getStatusOptions,
  getTitleOptions,
  interestedModelOptions,
  testDriveTimeOptions,
} from '../../../constants';

export const createLeadFormFields = (
  prospect?: Prospect,
  onCancel?: () => void,
  organizationName?: string,
  labels?: Record<string, string>,
  locale = 'en-US'
): FormField[] => [
  {
    id: 'personalInformation',
    fieldType: 'group',
    width: 'full',
    title: '',
    children: [
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'prospectOwner',
        label: labels?.['form.prospectOwner'] ?? 'Prospect Owner',
        content: prospect?.prospectRecordOwner || '-',
      },
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'prospectOwnerLocation',
        label: labels?.['form.prospectOwnerLocation'] ?? 'Prospect Owner Location',
        content: prospect?.prospectOwnerLocation || '-',
      },
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'prospectCaptured',
        label: labels?.['form.prospectCaptured'] ?? 'Prospect Captured',
        content: formatDateTime(prospect?.createdDatetime, locale),
      },
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'lastModified',
        label: labels?.['form.lastModified'] ?? 'Last Modified',
        content: formatDateTime(prospect?.updatedDatetime, locale),
      },
    ],
  },
  // Personal Information Section
  {
    id: 'informationGroup',
    fieldType: 'group',
    title: '',
    width: 'full',
    children: [
      {
        id: 'leadFirstName',
        fieldType: 'text',
        name: 'firstName',
        apiKey: 'prospectFirstName',
        label: labels?.['form.firstName'] ?? 'First Name',
        placeholder: labels?.['form.firstNamePlaceholder'] ?? 'Enter First Name',
        defaultValue: prospect?.prospectFirstName || '',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: labels?.['form.firstNameRequired'] ?? 'First name is required',
        },
      },
      {
        id: 'prospectMiddleName',
        fieldType: 'text',
        name: 'prospectMiddleName',
        apiKey: 'prospectMiddleName',
        label: labels?.['form.middleName'] ?? 'Middle Name',
        width: 'quarter',
        placeholder: labels?.['form.middleNamePlaceholder'] ?? 'Enter Middle Name',
        defaultValue: prospect?.prospectMiddleName || '',
      },
      {
        id: 'leadLastName',
        fieldType: 'text',
        name: 'lastName',
        apiKey: 'prospectLastName',
        label: labels?.['form.lastName'] ?? 'Last Name',
        placeholder: labels?.['form.lastNamePlaceholder'] ?? 'Enter Last Name',
        defaultValue: prospect?.prospectLastName || '',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: labels?.['form.lastNameRequired'] ?? 'Last name is required',
        },
      },
      {
        id: 'prospectTitle',
        fieldType: 'dropdown',
        name: 'prospectTitle',
        apiKey: 'prospectTitle',
        label: labels?.['form.title'] ?? 'Title',
        placeholder: labels?.['form.select'] ?? 'Select',
        defaultValue: prospect?.prospectTitle || '',
        required: true,
        width: 'quarter',
        options: getTitleOptions(labels, locale),
        validationMessages: {
          required: labels?.['form.titleRequired'] ?? 'Title is required',
        },
      },
      {
        id: 'leadEmail',
        fieldType: 'email',
        name: 'email',
        apiKey: 'prospectEmail',
        label: labels?.['form.email'] ?? 'Email',
        defaultValue: prospect?.prospectEmail || '',
        required: true,
        disabled: true,
        width: 'half',
        validationMessages: {
          required: labels?.['form.emailRequired'] ?? 'Email is required',
          pattern: labels?.['form.emailAddressInvalid'] ?? 'Please enter a valid email address',
        },
      },
      {
        id: 'leadPhone',
        fieldType: 'phone',
        name: 'phone',
        apiKey: 'prospectPhone',
        label: labels?.['form.phone'] ?? 'Phone',
        value: prospect?.prospectPhone || '',
        required: false,
        width: 'half',
        validationMessages: {
          pattern: labels?.['form.phoneNumberInvalid'] ?? 'Please enter a valid phone number',
        },
      },
      {
        id: 'zipCode',
        fieldType: 'text',
        name: 'zipCode',
        apiKey: 'zipCode',
        label: labels?.['form.postalCode'] ?? 'Postal Code',
        placeholder: labels?.['form.postalCodePlaceholder'] ?? 'Enter Postal Code',
        defaultValue: prospect?.zipCode || '',
        required: true,
        width: 'third',
        postalCodeAutoPopulate: {
          cityField: 'city',
          countryField: 'country',
        },
        validationMessages: {
          required: labels?.['form.postalCodeRequired'] ?? 'Postal code is required',
        },
      },
      {
        id: 'city',
        fieldType: 'text',
        name: 'city',
        apiKey: 'city',
        label: labels?.['form.city'] ?? 'City',
        placeholder: labels?.['form.cityPlaceholder'] ?? 'Enter City',
        defaultValue: prospect?.city || prospect?.region || '',
        required: true,
        width: 'third',
        validationMessages: {
          required: labels?.['form.cityRequired'] ?? 'City is required',
          pattern: labels?.['form.cityNameInvalid'] ?? 'Please enter a valid city name',
        },
      },
      {
        id: 'country',
        fieldType: 'text',
        name: 'country',
        apiKey: 'countryCode',
        label: labels?.['form.country'] ?? 'Country',
        placeholder: '',
        defaultValue: prospect?.countryCode || '',
        disabled: true,
        width: 'third',
      },
      {
        id: 'sourceOfProspect',
        fieldType: 'content',
        label: labels?.['form.sourceOfProspect'] ?? 'Source of Prospect',
        width: 'full',
        content:
          prospect?.prospectCaptureForm ||
          prospect?.prospectSourceName ||
          prospect?.sourceName ||
          '-',
      },
    ],
  },
  // Purchase Profile Section
  {
    id: 'interestedModel',
    fieldType: 'dropdown',
    name: 'interestedModel',
    apiKey: 'interestedTrim',
    label: labels?.['form.interestedModeltrim'] ?? 'Interested Model/Trim',
    placeholder: labels?.['form.aModelPlaceholder'] ?? 'Select a Model',
    defaultValue: prospect?.interestedTrim || '',
    options: interestedModelOptions,
  },
  {
    id: 'interestedVehicleCondition',
    fieldType: 'radio_group',
    name: 'interestedVehicleCondition',
    apiKey: 'interestedVehicleCondition',
    label: labels?.['form.interestedVehicleCondition'] ?? 'Interested Vehicle Condition',
    width: 'full',
    layout: 'horizontal',
    options: [
      {
        label: labels?.['form.newVehicle'] ?? 'New Vehicle',
        value: 'new',
        default:
          !prospect?.interestedVehicleCondition || prospect?.interestedVehicleCondition === 'new',
      },
      {
        label: labels?.['form.usedVehicle'] ?? 'Used Vehicle',
        value: 'used',
        default: prospect?.interestedVehicleCondition === 'used',
      },
    ],
  },
  {
    id: 'currentCarMake',
    fieldType: 'dropdown',
    name: 'currentCarMake',
    apiKey: 'currentCarMake',
    label: labels?.['form.makeOfCurrent'] ?? 'Make of Current Car',
    placeholder: labels?.['form.makePlaceholder'] ?? 'Select Make',
    defaultValue: prospect?.currentCarMake || '',
    width: 'full',
    options: carMakerOptions,
  },
  {
    id: 'purchaseType',
    fieldType: 'dropdown',
    name: 'purchaseType',
    apiKey: 'purchaseType',
    label: labels?.['form.purchaseType'] ?? 'Purchase Type',
    placeholder: labels?.['form.purchaseTypePlaceholder'] ?? 'Select Purchase Type',
    defaultValue: prospect?.purchaseType || '',
    width: 'full',
    options: getPurchaseTypeOptions(labels),
  },
  {
    id: 'financeType',
    fieldType: 'dropdown',
    name: 'financeType',
    apiKey: 'financeType',
    label: labels?.['form.finance'] ?? 'Finance',
    placeholder: labels?.['form.financeTypePlaceholder'] ?? 'Select Finance Type',
    defaultValue: prospect?.financeType || '',
    width: 'full',
    options: getFinanceTypeOptions(labels),
  },
  {
    id: 'purchaseTiming',
    fieldType: 'dropdown',
    name: 'purchaseTiming',
    apiKey: 'purchaseTiming',
    label: labels?.['form.timing'] ?? 'Timing',
    placeholder: labels?.['form.timePlaceholder'] ?? 'Select time',
    defaultValue: prospect?.purchaseTiming || '',
    width: 'full',
    required: true,
    options: getPurchaseTimingOptions(labels),
    validationMessages: {
      required: labels?.['form.timingRequired'] ?? 'Timing is required',
    },
  },
  {
    id: 'nextEngagementDate',
    fieldType: 'date_picker',
    name: 'nextEngagementDate',
    apiKey: 'nextEngagementDate',
    label: labels?.['form.nextEngagementDate'] ?? 'Next Engagement Date',
    required: true,
    width: 'full',
    size: 'medium',
    minDate: 'today',
    defaultValue: getNextEngagementDate(prospect),
    validationMessages: {
      required: labels?.['form.nextEngagementDateRequired'] ?? 'Next Engagement Date is required',
    },
  },
  {
    id: 'preferredContactMethod',
    fieldType: 'dropdown',
    name: 'preferredContactMethod',
    apiKey: 'preferredContactMethod',
    label: labels?.['form.preferredContactMethod'] ?? 'Preferred Contact Method',
    placeholder:
      labels?.['form.preferredContactMethodPlaceholder'] ?? 'Select Preferred Contact Method',
    defaultValue: prospect?.preferredContactMethod || '',
    width: 'full',
    options: getPreferredContactMethodOptions(labels),
  },
  // Consent Section
  {
    id: 'emailConsent',
    fieldType: 'checkbox_group',
    name: 'isEmailOptIn',
    apiKey: 'isEmailOptIn',
    label: labels?.['form.emailConsent'] ?? 'Email Consent',
    width: 'full',
    layout: 'horizontal',
    badge: prospect?.isEmailOptOut
      ? {
          label: labels?.['form.optedOut'] ?? 'Opted Out',
          color: 'red' as const,
          type: 'secondary' as const,
        }
      : prospect?.isEmailOptIn == null
        ? {
            label: labels?.['form.notCaptured'] ?? 'Not Captured',
            color: 'darktaupe' as const,
            type: 'secondary' as const,
          }
        : prospect.isEmailOptIn && prospect.isEmailDoubleOptIn
          ? {
              label: labels?.['form.doubleOptedIn'] ?? 'Double Opted In',
              color: 'teal' as const,
              type: 'secondary' as const,
            }
          : prospect.isEmailOptIn && !prospect.isEmailDoubleOptIn
            ? {
                label: labels?.['form.doubleOptInPending'] ?? 'Double Opt In Pending',
                color: 'indigo' as const,
                type: 'secondary' as const,
              }
            : undefined,
    options: getEmailConsentOptions(labels),
    defaultValue: prospect?.isEmailOptIn ? ['emailOptIn'] : [],
    disabled: prospect?.isEmailOptIn === true,
  },
  {
    id: 'callConsent',
    fieldType: 'radio_group',
    name: 'callConsent',
    apiKey: 'callConsent',
    label: labels?.['form.callConsent'] ?? 'Call Consent',
    width: 'full',
    layout: 'horizontal',
    badge: prospect?.isCallOptIn
      ? {
          label: labels?.['form.optedIn'] ?? 'Opted In',
          color: 'teal' as const,
          type: 'secondary' as const,
        }
      : prospect?.isCallOptOut
        ? {
            label: labels?.['form.optedOut'] ?? 'Opted Out',
            color: 'red' as const,
            type: 'secondary' as const,
          }
        : prospect?.isCallOptIn == null && prospect?.isCallOptOut == null
          ? {
              label: labels?.['form.notCaptured'] ?? 'Not Captured',
              color: 'darktaupe' as const,
              type: 'secondary' as const,
            }
          : undefined,
    options: getPhoneConsentOptions(labels),
    defaultValue: prospect?.isCallOptIn ? 'callOptIn' : prospect?.isCallOptOut ? 'callOptOut' : '',
    disabledDependency: { name: 'phone', negate: true },
  },
  {
    id: 'notes',
    fieldType: 'textarea',
    name: 'notes',
    apiKey: 'noteText',
    label: labels?.['form.notes'] ?? 'Notes',
    placeholder: labels?.['form.notesPlaceholder'] ?? 'Enter Notes',
    defaultValue: getLatestNoteContent(prospect?.notes) || prospect?.noteText || '',
    width: 'full',
  },
  {
    apiKey: 'terms',
    fieldType: 'disclaimers',
    id: 'terms',
    name: 'terms',
    required: false,
    terms:
      labels?.['form.termsDisclaimer']?.replace(
        '{{organizationName}}',
        escapeHtml(organizationName || '-')
      ) ??
      `By clicking Save, I confirm that I have informed the prospect that their personal data will be processed by ${escapeHtml(organizationName || '-')}, Lucid Europe B.V., 
    and other relevant Lucid entities (such as local affiliates, importers, and dealers) 
    in accordance with <a href="https://lucidmotors.com/de-de/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Lucid's Privacy Policy</a> and, where applicable, the Agent's own privacy policy.`,
  },
  {
    id: 'formButtons',
    fieldType: 'buttonGroup',
    align: 'spaceBetween',
    buttons: [
      {
        id: 'cancelButton',
        label: labels?.['form.cancel'] ?? 'Cancel',
        size: 'medium',
        type: 'secondary',
        color: 'neutral',
        theme: 'dark',
        isFluid: true,
        onClick: onCancel,
      },
      {
        id: 'submitButton1',
        label: labels?.['form.save'] ?? 'Save',
        type: 'primary',
        size: 'medium',
        color: 'neutral',
        theme: 'light',
        isFluid: true,
        disabled: prospect?.statusName?.toLowerCase() === 'closed',
      },
    ],
  },
];

// formatTimeSlot is now imported from tools.ts as utcToLocalTimeSlot
// It converts UTC times back to studio local time based on locale

/**
 * Creates form fields for Test Drive tab
 * @param onCancel - Cancel button handler
 * @param testDriveSchedule - The test drive schedule data (optional)
 * @param organizationName - Organization name for disclaimer
 * @param labels - i18n labels
 * @returns FormField array for test drive form
 */
export const createTestDriveFormFields = (
  prospect?: Prospect,
  onCancel?: () => void,
  testDriveSchedule?: TestDriveSchedule,
  organizationName?: string,
  labels?: Record<string, string>,
  locale = 'en-us',
  partnerOrgLocation?: string
): FormField[] => {
  const isTestDriveCompleted = testDriveSchedule?.testDriveStatus === 'completed';

  return [
    {
      id: 'testDriveHeader',
      fieldType: 'group',
      width: 'full',
      title: labels?.['form.scheduleTestDrive'] ?? 'Schedule Test Drive',
      children: [
        {
          id: 'testDriveNow',
          fieldType: 'checkbox_group',
          name: 'testDriveNow',
          apiKey: 'testDriveNow',
          label: labels?.['form.testDriveNow'] ?? 'Test Drive Now',
          width: 'half',
          layout: 'horizontal',
          options: [{ label: labels?.['form.yes'] ?? 'Yes', value: 'yes' }],
          defaultValue: testDriveSchedule?.isActive ? ['yes'] : [],
          disabled: isTestDriveCompleted,
        },
        {
          fieldType: 'content',
          width: 'half',
          id: 'location',
          label: labels?.['form.location'] ?? 'Location',
          content:
            testDriveSchedule?.appointmentLocationName ||
            partnerOrgLocation ||
            prospect?.prospectOwnerLocation ||
            '-',
        },
        {
          id: 'scheduledDate',
          fieldType: 'date_picker',
          name: 'scheduledDate',
          apiKey: 'scheduledDate',
          label: labels?.['form.scheduledDate'] ?? 'Scheduled Date',
          placeholder: labels?.['form.datePlaceholder'] ?? 'Select Date',
          required: true,
          width: 'half',
          size: 'medium',
          minDate: 'today',
          defaultValue: utcToLocalDate(testDriveSchedule?.scheduledStartDatetime, locale),
          disabled: isTestDriveCompleted,
          dependency: {
            name: 'testDriveNow',
            value: 'yes',
          },
          validationMessages: {
            required: labels?.['form.scheduledDateRequired'] ?? 'Scheduled date is required',
          },
        },
        {
          id: 'scheduledTime',
          fieldType: 'dropdown',
          name: 'scheduledTime',
          apiKey: 'scheduledTime',
          label: labels?.['form.scheduledTime'] ?? 'Scheduled Time',
          placeholder: labels?.['form.timePlaceholder'] ?? 'Select Time',
          required: true,
          width: 'half',
          defaultValue: utcToLocalTimeSlot(
            testDriveSchedule?.scheduledStartDatetime,
            testDriveSchedule?.scheduledEndDatetime,
            locale
          ),
          options: testDriveTimeOptions,
          disabled: isTestDriveCompleted,
          dependency: {
            name: 'testDriveNow',
            value: 'yes',
          },
          validationMessages: {
            required: labels?.['form.scheduledTimeRequired'] ?? 'Scheduled time is required',
          },
        },
        {
          id: 'status',
          fieldType: 'dropdown',
          name: 'status',
          apiKey: 'status',
          label: labels?.['form.status'] ?? 'Status',
          placeholder: labels?.['form.statusPlaceholder'] ?? 'Select Status',
          required: true,
          width: 'full',
          defaultValue: testDriveSchedule?.testDriveStatus?.toLowerCase() || 'scheduled',
          options: getStatusOptions(labels),
          disabled: isTestDriveCompleted,
          dependency: {
            name: 'testDriveNow',
            value: 'yes',
          },
          validationMessages: {
            required: labels?.['form.statusRequired'] ?? 'Status is required',
          },
        },
        {
          id: 'selectedModelTrim',
          fieldType: 'dropdown',
          name: 'selectedModelTrim',
          apiKey: 'selectedModelTrim',
          label: labels?.['form.selectedModeltrim'] ?? 'Selected Model/Trim',
          placeholder: labels?.['form.aModelPlaceholder'] ?? 'Select a Model',
          width: 'full',
          defaultValue: testDriveSchedule?.trim || testDriveSchedule?.model || '',
          options: interestedModelOptions,
          disabled: isTestDriveCompleted,
          dependency: {
            name: 'testDriveNow',
            value: 'yes',
          },
        },
      ],
    },
    {
      apiKey: 'terms',
      fieldType: 'disclaimers',
      id: 'terms',
      name: 'terms',
      required: false,
      terms:
        labels?.['form.termsDisclaimer']?.replace(
          '{{organizationName}}',
          escapeHtml(organizationName || '-')
        ) ??
        `By clicking Save, I confirm that I have informed the prospect that their personal data will be processed by ${escapeHtml(organizationName || '-')}, Lucid Europe B.V., 
    and other relevant Lucid entities (such as local affiliates, importers, and dealers) 
    in accordance with <a href="https://lucidmotors.com/de-de/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Lucid's Privacy Policy</a> and, where applicable, the Agent's own privacy policy.`,
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      align: 'spaceBetween',
      buttons: [
        {
          id: 'cancelButton',
          label: labels?.['form.cancel'] ?? 'Cancel',
          size: 'medium',
          type: 'secondary',
          color: 'neutral',
          theme: 'dark',
          isFluid: true,
          onClick: onCancel,
          disabled: isTestDriveCompleted,
        },
        {
          id: 'submitButton',
          label: labels?.['form.save'] ?? 'Save',
          type: 'primary',
          size: 'medium',
          color: 'neutral',
          theme: 'light',
          isFluid: true,
          disabled: prospect?.statusName?.toLowerCase() === 'closed' || isTestDriveCompleted,
        },
      ],
    },
  ];
};
